import { parsePhoneNumber } from "libphonenumber-js";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { Config } from "../../../../shared/core/config/config";
import type { FormEmailInput } from "../../../../shared/core/data-forms/form-input-types";
import { FormInputType } from "../../../../shared/core/data-forms/form-input-types";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import {
  authenticationManager,
  customerActionService,
  featuresManager,
  identityManager,
} from "../../../../shared/core/service/services";
import { formatAddress } from "../../../../shared/domains/client/address";
import { useClientLinks } from "../../../../shared/domains/client/use-client-links";
import { KYC_EXCLUDES } from "../../../../shared/domains/identity/identity";
import { isDefined } from "../../../../shared/utils/assert";
import { getLocaleDateFromUtcDateString } from "../../../../shared/utils/date-to-local";
import { useObservable } from "../../../../shared/utils/observable";
import { filterObjectKeys, getGenderByInt, isObjectEmpty, useAsyncEffect } from "../../../../shared/utils/utils";
import { Path } from "../../../core/routing/path";
import { useClient } from "../../../domain/authentication/use-client";
import { UnstyledButton } from "../../common/buttons/default-button";
import { BluePrimaryButton, WhitePrimaryButton } from "../../common/buttons/primary-button";
import { CloseIcon } from "../../common/close-icon";
import { EmailField } from "../../common/data-forms/email-field";
import { ErrorMessage } from "../../common/error-message";
import { PageBackLink } from "../../common/page/page-back-header";
import { Spinner } from "../../common/spinner";
import { theme } from "../../styles/theme";
import { DownloadDocument } from "./components/download-document";
import { InformationSection } from "./components/information-section";

const CheckIcon = require("../../../assets/images/svg/check-circle.svg");

export const MyProfileScreen = () => {
  const { formatMessage, formatDate } = useIntl();
  const { client } = useClient();
  const { push } = useHistory();
  const emailRef = React.useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [editingEmail, setEditingEmail] = React.useState(false);
  const features = useObservable(featuresManager.features);
  const { updateEmailAddressLink, canUpdateEmailAddress } = useClientLinks();
  const [successMsg, setSuccessMsg] = React.useState<string | null>();
  const [userEmail, setUserEmail] = React.useState<FormEmailInput>({
    type: FormInputType.Email,
    id: "email",
    label: "Email",
    value: client?.email || "",
  });

  const clientRole = useObservable(identityManager.clientRole);
  const kycLabels = useObservable(identityManager.kycLabels);
  const kycDocuments = useObservable(identityManager.kycDocuments);
  const clientRoleLoading = useObservable(identityManager.clientRoleLoading);
  const kycDocumentsLoading = useObservable(identityManager.kycDocumentsLoading);
  const clientRoleError = useObservable(identityManager.clientRoleError);
  const kycDocumentsError = useObservable(identityManager.kycDocumentsError);
  const downloadKycError = useObservable(identityManager.downloadKycError);
  const kycLabelsError = useObservable(identityManager.kycLabelsError);

  const [clientKyc, setClientKyc] = React.useState<object>();
  const [viewKycDetails, setViewKycDetails] = React.useState(false);

  const session = authenticationManager.session.get();

  useAsyncEffect(async () => {
    if (Config.DISPLAY_KYC_DATA && features.identityView) {
      await identityManager.fetchClientRole(session.clientId!);
    }
  }, []);

  useAsyncEffect(async () => {
    if (Config.DISPLAY_KYC_DATA && clientRole && features.additionalDataRead) {
      await identityManager.fetchKycLabels(clientRole.profileId);
    }
  }, [clientRole]);

  useAsyncEffect(async () => {
    if (Config.DISPLAY_KYC_DATA && features.identityDocumentView) {
      await identityManager.fetchKycDocuments(session.clientId!);
    }
  }, [session]);

  useEffect(() => {
    clientRole && setClientKyc(filterObjectKeys(clientRole.kyc, KYC_EXCLUDES));
  }, [clientRole]);

  const handleEditEmail = () => {
    setEditingEmail(!editingEmail);
    setUserEmail({
      ...userEmail,
      value: client?.email || "",
    });
    setTimeout(() => {
      if (emailRef.current) {
        emailRef.current.focus();
      }
    }, 100);
  };

  const handleSetUserEmail = (value: string) => {
    setUserEmail({
      ...userEmail,
      value,
    });
  };

  const handleSubmitNewEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (client && features?.sdaCustomerOnboardingActionsInitiate && userEmail.value !== client.email) {
      const url = updateEmailAddressLink(client);
      if (url) {
        setIsSubmitting(true);
        emailRef.current?.blur();
        try {
          await customerActionService.makeCustomerAction(url, { email: userEmail.value });
          setSuccessMsg(formatMessage("clientInformation.emailSuccess"));
        } catch (e) {
          console.log("catch", e);
        } finally {
          handleEditEmail();
          setIsSubmitting(false);
        }
      }
    } else {
      handleEditEmail();
    }
  };

  const toggleKycDetails = () => {
    setViewKycDetails((prev) => !prev);
  };

  return (
    <>
      {!!successMsg && (
        <SuccessMsg>
          <img src={CheckIcon} alt="" />
          {successMsg}
          <CloseButton onClick={() => setSuccessMsg(null)}>
            <CloseIcon />
          </CloseButton>
        </SuccessMsg>
      )}
      <PageBackLink onBack={() => push(Path.ProfileMenu)} />
      {isDefined(client) && (
        <Panel>
          <PanelTitle>{formatMessage("clientInformation.title")}</PanelTitle>
          {client.birthDate ? (
            <InformationSection
              label={formatMessage("clientInformation.birthDateTitle")}
              value={formatDate(getLocaleDateFromUtcDateString(client.birthDate), {
                year: "numeric",
                month: "long",
                day: "2-digit",
              })}
            />
          ) : null}
          <InformationSection label={formatMessage("clientInformation.birthPlaceTitle")} value={client.birthPlace} />
          <InformationSection
            label={formatMessage("clientInformation.addressTitle")}
            value={formatAddress(client.address)}
          />
          <InformationSection
            label={formatMessage("clientInformation.phoneTitle")}
            value={client.contactphone ? parsePhoneNumber(`+${client.contactphone}`).formatInternational() : undefined}
            isPhone={true}
          />
          <InformationSection
            label={formatMessage("clientInformation.emailTitle")}
            value={client.email}
            onEdit={
              canUpdateEmailAddress(client) && features?.sdaCustomerOnboardingActionsInitiate
                ? () => handleEditEmail()
                : undefined
            }
          >
            {editingEmail ? (
              <EmailForm
                $submitting={isSubmitting}
                onSubmit={(e) => (canUpdateEmailAddress(client) ? handleSubmitNewEmail(e) : null)}
              >
                <EmailFieldWrapper>
                  <EmailField ref={emailRef} input={userEmail} onChange={handleSetUserEmail} />
                </EmailFieldWrapper>
                <StyledActionWrapper>
                  <CancelButton type="button" size="S" onClick={handleEditEmail}>
                    {formatMessage("common.cancel")}
                  </CancelButton>
                  <SubmitButton disabled={isSubmitting} type="submit" size="S">
                    {formatMessage("common.validate")}
                  </SubmitButton>
                </StyledActionWrapper>
              </EmailForm>
            ) : null}
          </InformationSection>
          {clientKyc &&
            viewKycDetails &&
            Object.keys(clientKyc).map((key) => {
              let value = clientKyc[key];
              if (["gender", "personal_gender"].includes(key)) {
                value = formatMessage(`gender.${getGenderByInt(value)}`);
              } else if (typeof value === "boolean") {
                value = String(value);
              } else if (typeof value === "object") {
                value = value.label;
              }
              return <InformationSection key={key} label={kycLabels?.[key] || key} value={value} />;
            })}

          {clientRoleLoading && <Spinner />}
          {clientKyc && !isObjectEmpty(clientKyc) && (
            <ViewMoreButton
              type="button"
              onClick={(e) => {
                e.currentTarget.blur();
                toggleKycDetails();
              }}
            >
              {viewKycDetails
                ? formatMessage("clientInformation.viewLess")
                : formatMessage("clientInformation.viewMore")}
            </ViewMoreButton>
          )}
        </Panel>
      )}

      {kycDocuments.length === 0 && kycDocumentsLoading && <Spinner />}
      {kycDocuments.length > 0 && (
        <Panel>
          <PanelTitle>{formatMessage("documents.title")}</PanelTitle>
          {kycDocumentsLoading && <Spinner style={{ marginBottom: 10 }} />}
          {kycDocuments.map((document) => (
            <DownloadDocument key={document.id} document={document} />
          ))}
        </Panel>
      )}

      {clientRoleError?.message && <ErrorMessage>{clientRoleError.message}</ErrorMessage>}
      {kycDocumentsError?.message && <ErrorMessage>{kycDocumentsError.message}</ErrorMessage>}
      {downloadKycError?.message && <ErrorMessage>{downloadKycError.message}</ErrorMessage>}
      {kycLabelsError?.message && <ErrorMessage>{kycLabelsError.message}</ErrorMessage>}
    </>
  );
};

/* One white surface holding the whole block, as the mockup draws it: a form the holder reads. */
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-self: stretch;
  width: 100%;
  max-width: 560px;
  margin-top: 12px;
  margin-bottom: 20px;
  padding: 28px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 16px;
  background-color: #ffffff;
`;

const PanelTitle = styled.h1`
  ${theme.boldText};
  margin: 0;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const EmailForm = styled.form<{ $submitting: boolean }>`
  display: flex;
  flex-direction: column;
  pointer-events: ${({ $submitting }) => ($submitting ? "none" : "auto")};
`;

/* The shared input keeps its own look everywhere else; the panel outlines it here. */
const EmailFieldWrapper = styled.div`
  > div {
    width: 100%;
    border: 1px solid ${theme.colors.gray[500]};
    border-radius: 8px;
    background-color: #ffffff;
  }

  input {
    ${theme.text};
    font-size: 15px;
    color: ${theme.colors.blue[800]};
  }
`;

const StyledActionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 12px;
  gap: 10px;
`;

const SubmitButton = styled(BluePrimaryButton)`
  padding: 8px 20px;
  box-shadow: none;
`;

const CancelButton = styled(WhitePrimaryButton)`
  padding: 8px 20px;
  border: 1px solid ${theme.colors.blue[700]};
  box-shadow: none;
  color: ${theme.colors.blue[700]};
`;

const ViewMoreButton = styled(UnstyledButton)`
  ${theme.text};
  align-self: flex-start;
  font-size: 14px;
  line-height: 18px;
  text-decoration: underline;
  color: ${theme.colors.blue[700]};
`;

const SuccessMsg = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  align-self: stretch;
  max-width: 560px;
  margin-bottom: 20px;
  padding: 15px 20px;
  border-radius: 12px;
  background-color: ${theme.colors.info[300]};
  ${theme.mediumText};
  font-size: 0.875rem;
  color: ${theme.colors.info[500]};
`;

const CloseButton = styled(UnstyledButton)`
  display: flex;
  margin-left: auto;
`;
